<template>
  <div class="co-lead-loss-fund" ref="rootEl">
    <!-- Loss fund is SECONDARY on this step; the focus is the cash call amounts
         below. So we MINIMISE it: only the per-UMR selector boxes show by
         default (styled like the figures), NO box is selected, and the loss fund
         VALUES appear only once the user clicks a box above. -->
    <!-- Rendered as a <label> (not a styled <p>) so it INHERITS exactly the same
         font as the "Initial Loss Fund Value" label below, which is a plain
         <label> with no font overrides of its own. -->
    <label class="co-lead-loss-fund__heading">Loss Fund by UMR</label>

    <ul class="co-lead-loss-fund__tabs">
      <li
          v-for="(row, index) in rows"
          :key="row.bdx_key[0]"
          :class="{ 'co-lead-loss-fund__tab--active': index === activeIndex }"
          @click="toggleTab(index)"
      >
        {{ row.tpa || '—' }} — {{ row.umr || (row.claimref && row.claimref[0]) || '—' }}
      </li>
    </ul>

    <!-- Values only render once a box above is selected. -->
    <div v-if="activeRow" class="co-lead-loss-fund__panel">
      <b-row class="row mb-2">
        <b-col lg="6">
          <BaseInputCurrency
              :value="activeRow.initial_loss_fund_value"
              @input="updateActiveRow('initial_loss_fund_value', $event)"
              :displayType="displayflex"
              label="Initial Loss Fund Value"
              size="sm"
          />
        </b-col>
        <b-col lg="6">
          <BaseInputCurrency
              :value="activeRow.current_loss_fund_balance"
              @input="updateActiveRow('current_loss_fund_balance', $event)"
              :displayType="displayflex"
              label="Current Loss Fund Balance"
              size="sm"
          />
        </b-col>
      </b-row>
    </div>
    <hr class="co-lead-loss-fund__divider" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { BaseInputCurrency } from '@components/base'

export default defineComponent({
  name: 'CashCallCoLeadLossFundTabs',
  components: {
    BaseInputCurrency
  },
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const rows = computed(() => props.value)
    const rootEl = ref(null)
    // No box selected by default, so the loss fund VALUES stay hidden until the
    // user clicks a UMR box above (keeps the section minimised).
    const activeIndex = ref(null)
    const activeRow = computed(() =>
      activeIndex.value === null ? null : (rows.value[activeIndex.value] || null)
    )

    // Click a box to reveal its values; click the same box again to hide them.
    const toggleTab = (index) => {
      activeIndex.value = activeIndex.value === index ? null : index
    }

    // True when a row has at least one loss fund value entered. "0" counts as a
    // real entered value; blank/null/undefined do not.
    const hasLossFundValues = (row) => {
      if (!row) return false
      const filled = (v) =>
        v !== null && v !== undefined && String(v).trim() !== ''
      return filled(row.initial_loss_fund_value) ||
             filled(row.current_loss_fund_balance)
    }

    // Clicking anywhere OUTSIDE this loss fund section (e.g. into the Indemnity
    // field below) collapses the panel again - keeping the section minimised
    // once the user moves on. BUT if the user has actually entered a loss fund
    // value, keep it visible so their data stays on screen; it only collapses
    // once both boxes are empty again (e.g. they clear the values).
    const onDocumentClick = (evt) => {
      if (activeIndex.value === null) return
      if (hasLossFundValues(activeRow.value)) return
      const el = rootEl.value
      if (el && !el.contains(evt.target)) {
        activeIndex.value = null
      }
    }
    onMounted(() => document.addEventListener('mousedown', onDocumentClick))
    onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentClick))

    const updateActiveRow = (field, newValue) => {
      if (activeIndex.value === null) return
      const next = rows.value.slice()
      next[activeIndex.value] = { ...next[activeIndex.value], [field]: newValue }
      emit('input', next)
    }

    // Same layout flag the normal cash call passes to BaseInputCurrency to get
    // the compact "label on the left, input on the right" row style.
    const displayflex = 'displayflex'

    return {
      rows,
      rootEl,
      activeIndex,
      activeRow,
      toggleTab,
      updateActiveRow,
      displayflex
    }
  }
})
</script>

<style scoped>
/* Heading matches the "Initial Loss Fund Value" label exactly: it's the same
   element type (<label>) with NO font overrides, so it inherits the app's label
   font-size / weight / colour rather than declaring its own. Only spacing is
   set here. */
.co-lead-loss-fund__heading {
  display: block;
  margin-bottom: 8px;
}

/* The component root is a BLOCK box, so it takes its parent's width rather than
   its content's.
   The ACTUAL cause of this section pushing the form off-screen was not here at
   all - it was `form { display: grid }` with no grid-template-columns in
   CashCallCreateModal.vue, whose implicit auto track sized to this strip's
   max-content. That is fixed there.
   `contain: inline-size` is kept as a local guard so this component can never
   inflate a future flex/grid ancestor the same way again: it stops anything
   inside from contributing to an ancestor's intrinsic width, which is the one
   thing width/min-width/max-width cannot do. Safe here because nothing in this
   component is absolutely positioned relative to the root. */
.co-lead-loss-fund {
  min-width: 0;
  max-width: 100%;
  width: 100%;
  contain: inline-size;
}

.co-lead-loss-fund__tabs {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  gap: 6px;
  /* With the form's grid track now pinned to minmax(0, 1fr), 100% here resolves
     against a box genuinely bounded by the step width, so overflow-x finally
     has a definite width to scroll within. */
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
}

/* Always show the scrollbar on this strip (rather than the OS overlay one that
   only appears while scrolling), so it is obvious there are more UMRs to the
   right when the list overflows. */
.co-lead-loss-fund__tabs {
  scrollbar-width: thin;
  scrollbar-color: #b9c6c8 transparent;
}

.co-lead-loss-fund__tabs::-webkit-scrollbar {
  height: 8px;
}

.co-lead-loss-fund__tabs::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 4px;
}

.co-lead-loss-fund__tabs::-webkit-scrollbar-thumb {
  background: #b9c6c8;
  border-radius: 4px;
}

.co-lead-loss-fund__tabs::-webkit-scrollbar-thumb:hover {
  background: #8fa3a6;
}

/* Per-UMR selector boxes: NO visible border by default (the border is
   transparent so the box is quiet and the section recedes). The teal UMR text
   is the only thing shown. */
.co-lead-loss-fund__tabs li {
  /* Compact: with many UMRs these are a selector strip, not headline text, so
     they sit a step below the body size and keep the row shallow. */
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 6px;
  white-space: nowrap;
  color: #1c4a50;
  font-weight: 600;
  background: #fff;
  /* Flex items shrink below their content width by default, so with many UMRs
     the labels were squeezed and the row spilled past the step instead of
     scrolling. Pin each tab to its own width and let the container scroll. */
  flex: 0 0 auto;
}

/* On click (selected): the border appears in the SAME teal as the UMR text.
   No fill/colour change - just the outline, so it isn't jarring. */
.co-lead-loss-fund__tabs li.co-lead-loss-fund__tab--active {
  border-color: #1c4a50;
}

.co-lead-loss-fund__panel {
  margin-top: 12px;
}
/* BaseInputCurrency's shared `displayflex` styling sizes the label at 50% and
   the input at 70% + 20px, i.e. wider than its own column. That overflow was
   stretching the whole Step 4 modal once the tab strip filled the row. Scope
   the widths back inside the column here rather than changing the shared
   component, which the normal cash call also uses. */
.co-lead-loss-fund__panel ::v-deep .labelWidth {
  width: auto;
  flex: 0 0 auto;
  white-space: nowrap;
  margin-right: 12px;
}

.co-lead-loss-fund__panel ::v-deep .textBoxWidth {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 0;
}

/* Divider below the loss-fund fields, matching the normal cash call layout. */
.co-lead-loss-fund__divider {
  border: 0;
  border-top: 2px solid #e9ecef;
  margin: 8px 0 4px;
}
</style>
